/**
 * Générateur — Il calcule avec les nombres rationnels, notamment dans le cadre
 * de résolution de problèmes.
 * Attendu ID : 202 | Niveau : 3ème | Domaine : Nombres (d11, O2)
 *
 * Source doc prof (NOMBRE ET CALCULS 3e, séances A3S1-A3S2). Plan de cartes
 * auto-validé 2026-07-16 (journal TODO_EXERCISEUR_3E.md §6) :
 *
 *   « somme »       — a/b ± c/d irréductible (2 trous num/den, signe au num.)
 *   « produit »     — simplifier AVANT de multiplier (2 trous)
 *   « quotient »    — ÷ fraction = × inverse, étape imposée (4 trous)
 *   « priorites »   — expression avec priorités (2 trous)
 *   « parentheses » — paire J/K avec/sans parenthèses (tableau V/F calculé)
 *   « piege »       — QCM pièges (sommes non simplifiables, signes, cubes)
 *   « partage »     — partage en cascade, fraction de fraction (trous)
 *   « remontee »    — remontée au tout (stade/salle d'étude) (trous)
 *
 * Toute l'arithmétique est EXACTE (toolkit rationnel entier de nombres.js,
 * réécrit indépendamment côté solveur). Fractions rendues en KaTeX
 * (FORMATS_TANOED §0.5bis), signe devant la barre, dénominateurs positifs.
 * Consignes : irréductible exigé, dit en phrase.
 *
 * ⚠️ Seul l'AFFICHAGE passe en KaTeX. Les trous restent des valeurs tapées au
 * clavier (« -13 », « 16 ») : l'élève remplit le numérateur et le dénominateur
 * d'une fraction dessinée, mais le scoring compare bien du texte brut.
 */

import {
    randomChoice, randInt, tex,
    makeQcm, makeTexteTrou, makeVraiFaux,
} from '../../../utils';
import { rat, ratAdd, ratSub, ratMul, ratDiv, ratEq, fracFrag, ratFrag, pgcd } from './nombres';

const ATTENDU_ID = 202;

const L_SOMME = 'Additionner ou soustraire deux fractions';
const L_PRODUIT = 'Multiplier des fractions en simplifiant avant';
const L_QUOTIENT = 'Diviser par une fraction';
const L_PRIORITES = 'Calculer une expression avec priorités';
const L_PARENTHESES = 'Le rôle des parenthèses dans un calcul de fractions';
const L_PIEGE = 'Déjouer les pièges du calcul de fractions';
const L_PARTAGE = 'Résoudre un partage en cascade';
const L_REMONTEE = 'Retrouver le tout à partir d\'une fraction';

const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// Tirage d'une fraction « propre » : q ∈ [2..qMax], p ∈ [1..pMax], réduite.
const fracAlea = (pMax, qMax, signee = false) => {
    const q = randInt(2, qMax);
    let p = randInt(1, pMax);
    if (signee && randInt(0, 1)) p = -p;
    return rat(p, q);
};

const LETTRES = ['A', 'B', 'C', 'D', 'G', 'H', 'K', 'M'];

// ═════════ « somme » — a/b ± c/d (2 trous, irréductible) ═════════

const genSommeTrous = () => {
    let x; let y; let r; let op;
    for (let essai = 0; essai < 60; essai++) {
        x = fracAlea(15, 16, true);
        y = fracAlea(15, 16, false);
        op = randomChoice(['+', '-']);
        r = op === '+' ? ratAdd(x, y) : ratSub(x, y);
        if (r.q > 1 && r.p !== 0 && Math.abs(r.p) <= 99 && r.q <= 99 && x.q !== y.q) break;
    }
    if (!(r.q > 1 && r.p !== 0)) { x = rat(-13, 8); y = rat(7, 16); op = '+'; r = ratAdd(x, y); } // repli (doc Ex.2)
    const lettre = randomChoice(LETTRES);
    const phrase = `$${lettre} = \\dfrac{{{${r.p}}}}{{{${r.q}}}}$`;
    const payload = makeTexteTrou(phrase,
        `<p>On pose ${tex(`${lettre} = ${ratFrag(x)} ${op === '+' ? '+' : '-'} ${ratFrag(y)}`)}.</p>`
        + '<p><b>Calcule et donne le résultat en fraction irréductible.</b> '
        + 'Si le résultat est négatif, écris le signe au numérateur.</p>',
        { ...opts(L_SOMME), mode: 'saisie' });
    payload._meta.params = { x, y, op, r };
    return payload;
};

// ═════════ « produit » — simplifier avant de multiplier (2 trous) ═════════

const genProduitTrous = () => {
    // Croisement de facteurs : x = (s·u1)/(t·v2), y = (t·v1)/(s·u2)
    // → x × y = (u1·v1)/(u2·v2) : la simplification AVANT le calcul s'impose.
    let x; let y; let r;
    for (let essai = 0; essai < 60; essai++) {
        const s = randInt(2, 7); const t = randInt(2, 7);
        const u1 = randInt(1, 5); const u2 = randInt(2, 5);
        const v1 = randInt(1, 5); const v2 = randInt(2, 5);
        x = { p: s * u1, q: t * v2 };            // NON réduits à l'affichage
        y = { p: t * v1, q: s * u2 };
        r = ratMul(rat(x.p, x.q), rat(y.p, y.q));
        const gx = pgcd(x.p, x.q); const gy = pgcd(y.p, y.q);
        if (r.q > 1 && r.p !== 0 && gx === 1 && gy === 1 && x.p !== y.p && x.q !== y.q
            && x.p <= 45 && x.q <= 45 && y.p <= 45 && y.q <= 45) break;
        r = null;
    }
    if (!r) { x = { p: 44, q: 105 }; y = { p: 42, q: 66 }; r = ratMul(rat(44, 105), rat(42, 66)); } // repli (doc Ex.3 G)
    const lettre = randomChoice(LETTRES);
    const phrase = `$${lettre} = \\dfrac{{{${r.p}}}}{{{${r.q}}}}$`;
    const payload = makeTexteTrou(phrase,
        `<p>On pose ${tex(`${lettre} = ${fracFrag(x.p, x.q)} \\times ${fracFrag(y.p, y.q)}`)}.</p>`
        + '<p>Simplifie <b>avant</b> de multiplier, puis donne le résultat '
        + 'en <b>fraction irréductible</b>.</p>',
        { ...opts(L_PRODUIT), mode: 'saisie' });
    payload._meta.params = { x, y, r };
    return payload;
};

// ═════════ « quotient » — ÷ fraction = × inverse (étape + résultat) ═════════

const genQuotientTrous = () => {
    let a; let b; let c; let d; let r = null;
    for (let essai = 0; essai < 80; essai++) {
        a = randInt(2, 30); b = randInt(3, 35); c = randInt(2, 36); d = randInt(2, 20);
        r = ratDiv(rat(a, b), rat(c, d));
        // Fractions AFFICHÉES déjà irréductibles et non entières (pas de 9/9, 28/7).
        if (c !== d && pgcd(a, b) === 1 && pgcd(c, d) === 1 && a !== b
            && r.q > 1 && r.p >= 1 && r.p <= 60 && r.q <= 60) break;
        r = null;
    }
    if (!r) { a = 24; b = 35; c = 36; d = 14; r = ratDiv(rat(a, b), rat(c, d)); } // repli (doc Ex.3 J)
    // L'étape « × inverse » est imposée : trous d puis c, puis résultat p/q.
    const phrase = `$J = \\dfrac{${a}}{${b}} \\times \\dfrac{{{${d}}}}{{{${c}}}} = \\dfrac{{{${r.p}}}}{{{${r.q}}}}$`;
    const payload = makeTexteTrou(phrase,
        `<p>On pose ${tex(`J = \\dfrac{${a}}{${b}} \\div \\dfrac{${c}}{${d}}`)}.</p>`
        + '<p>Diviser par une fraction, c\'est multiplier par son <b>inverse</b>. '
        + '<b>Complète l\'étape puis le résultat en fraction irréductible.</b></p>',
        { ...opts(L_QUOTIENT), mode: 'saisie' });
    payload._meta.params = { a, b, c, d, r };
    return payload;
};

// ═════════ « priorites » — x ± y × z (2 trous) ═════════

const genPrioritesTrous = () => {
    let x; let y; let z; let op; let r;
    for (let essai = 0; essai < 60; essai++) {
        x = fracAlea(14, 15, true);
        y = fracAlea(10, 15, false);
        z = fracAlea(10, 20, false);
        op = randomChoice(['+', '-']);
        const prod = ratMul(y, z);
        r = op === '+' ? ratAdd(x, prod) : ratSub(x, prod);
        if (r.q > 1 && r.p !== 0 && Math.abs(r.p) <= 99 && r.q <= 99 && prod.q > 1) break;
        r = null;
    }
    if (!r) { x = rat(-14, 15); y = rat(10, 15); z = rat(7, 20); op = '+'; r = ratAdd(x, ratMul(y, z)); } // repli (doc Ex.6 M)
    const lettre = randomChoice(LETTRES);
    const phrase = `$${lettre} = \\dfrac{{{${r.p}}}}{{{${r.q}}}}$`;
    const payload = makeTexteTrou(phrase,
        `<p>On pose ${tex(`${lettre} = ${ratFrag(x)} ${op === '+' ? '+' : '-'} ${ratFrag(y)} \\times ${ratFrag(z)}`)}.</p>`
        // L'indice « la multiplication d'abord » désamorçait l'erreur type visée
        // (calcul de gauche à droite) : la consigne rappelle seulement qu'il y a
        // des priorités à respecter.
        + '<p>Attention aux <b>priorités opératoires</b>. '
        + '<b>Donne le résultat en fraction irréductible.</b> '
        + 'Si le résultat est négatif, écris le signe au numérateur.</p>',
        { ...opts(L_PRIORITES), mode: 'saisie' });
    payload._meta.params = { x, y, z, op, r };
    return payload;
};

// ═════════ « parentheses » — J/K avec et sans parenthèses (V/F) ═════════

const genParenthesesVf = () => {
    let x; let y; let z; let avec; let sans;
    for (let essai = 0; essai < 60; essai++) {
        x = fracAlea(7, 8, false);
        y = fracAlea(9, 12, false);
        z = fracAlea(7, 6, false);
        // avec = (x − y) ÷ z ; sans = x − y ÷ z — différents PAR CONSTRUCTION.
        avec = ratDiv(ratSub(x, y), z);
        sans = ratSub(x, ratDiv(y, z));
        if (!ratEq(avec, sans) && avec.p !== 0 && sans.p !== 0
            && Math.abs(avec.p) <= 120 && avec.q <= 120 && Math.abs(sans.p) <= 120 && sans.q <= 120) break;
        avec = null;
    }
    if (!avec) { x = rat(1, 8); y = rat(7, 12); z = rat(7, 6); avec = ratDiv(ratSub(x, y), z); sans = ratSub(x, ratDiv(y, z)); } // repli (doc Ex.7)
    const sX = ratFrag(x); const sY = ratFrag(y); const sZ = ratFrag(z);
    const AVEC = `\\left(${sX} - ${sY}\\right) \\div ${sZ}`;
    const SANS = `${sX} - ${sY} \\div ${sZ}`;
    const lignes = []; const bonnes = [];
    // Ligne 1 : valeur de J (avec parenthèses) — vraie ou piégée par « sans ».
    if (randInt(0, 1)) { lignes.push(tex(`${AVEC} = ${ratFrag(avec)}`)); bonnes.push(0); }
    else { lignes.push(tex(`${AVEC} = ${ratFrag(sans)}`)); bonnes.push(1); }
    // Ligne 2 : valeur de K (sans parenthèses) — vraie ou piégée par « avec ».
    if (randInt(0, 1)) { lignes.push(tex(`${SANS} = ${ratFrag(sans)}`)); bonnes.push(0); }
    else { lignes.push(tex(`${SANS} = ${ratFrag(avec)}`)); bonnes.push(1); }
    // Ligne 3 : même valeur ? — FAUSSE par construction.
    lignes.push(`Les calculs ${tex(AVEC)} et ${tex(SANS)} ont la même valeur.`);
    bonnes.push(1);
    const payload = makeVraiFaux(lignes, bonnes,
        '<p>Mêmes nombres, avec ou sans parenthèses. <b>Vrai ou faux ?</b></p>',
        opts(L_PARENTHESES));
    payload._meta.params = { x, y, z, avec, sans, bonnes };
    return payload;
};

// ═════════ « piege » — QCM pièges (rotation de familles) ═════════

const genPiegeQcm = () => {
    const famille = randomChoice(['somme_simplif', 'signe', 'cubes']);
    if (famille === 'somme_simplif') {
        // (a+c)/(b+c) ≠ a/b : on ne simplifie PAS les sommes.
        let a; let b; let c; let correct;
        for (let essai = 0; essai < 40; essai++) {
            a = randInt(2, 9); b = a + randInt(1, 5); c = randInt(3, 15);
            correct = rat(a + c, b + c);
            if (pgcd(a, b) === 1 && !ratEq(correct, rat(a, b)) && correct.q > 1) break;
        }
        const choix = [tex(ratFrag(correct)), tex(fracFrag(a, b)), tex(ratFrag(rat(a + c, b))), tex(ratFrag(rat(a, b + c)))]
            .filter((v, i, t) => t.indexOf(v) === i);
        const payload = makeQcm(choix, tex(ratFrag(correct)),
            `<p><b>Que vaut ${tex(`\\dfrac{${a} + ${c}}{${b} + ${c}}`)} en fraction irréductible ?</b></p>`,
            opts(L_PIEGE));
        payload._meta.params = { famille, a, b, c };
        return payload;
    }
    if (famille === 'signe') {
        // Signe de -a/b + c/d sans tout calculer (comparaison des valeurs).
        let x; let y; let s;
        for (let essai = 0; essai < 40; essai++) {
            x = fracAlea(9, 14, false); y = fracAlea(9, 14, false);
            s = ratSub(y, x); // valeur de -x + y
            if (s.p !== 0 && !ratEq(x, y)) break;
        }
        const correct = s.p > 0 ? 'positif' : 'négatif';
        const payload = makeQcm(['positif', 'négatif', 'nul'], correct,
            `<p><b>Quel est le signe de ${tex(`-${ratFrag(x)} + ${ratFrag(y)}`)} ?</b></p>`,
            opts(L_PIEGE));
        payload._meta.params = { famille, x, y };
        return payload;
    }
    // cubes : (-a)³/(-b)³ = (a/b)³ > 0 (les signes s'annulent).
    let a; let b;
    do { a = randInt(2, 6); b = randInt(2, 6); } while (a === b || pgcd(a, b) !== 1);
    const v = ratDiv(rat(-a * a * a, 1), rat(-b * b * b, 1));
    const correct = tex(ratFrag(v));
    const choix = [correct, tex(`-${ratFrag(v)}`), tex(fracFrag(a, b)), tex(`-${fracFrag(a, b)}`)]
        .filter((val, i, t) => t.indexOf(val) === i);
    const payload = makeQcm(choix, correct,
        `<p><b>Que vaut ${tex(`\\dfrac{(-${a})^{3}}{(-${b})^{3}}`)} ?</b></p>`, opts(L_PIEGE));
    payload._meta.params = { famille, a, b };
    return payload;
};

// ═════════ « partage » — partage en cascade (fraction de fraction) ═════════

const PARTAGES = [
    // [b1 (part 1 = a1/b1), c/d (fraction de la part 1 pour la 2e)]
    { a1: 2, b1: 5, c: 3, d: 4 },   // doc : 2/5 puis 3/4 → 3/10, reste 3/10
    { a1: 1, b1: 4, c: 2, d: 3 },   // 1/4 puis 2/3 → 1/6, reste 7/12
    { a1: 1, b1: 3, c: 3, d: 4 },   // 1/3 puis 3/4 → 1/4, reste 5/12
    { a1: 2, b1: 5, c: 1, d: 2 },   // 2/5 puis 1/2 → 1/5, reste 2/5
    { a1: 1, b1: 2, c: 2, d: 5 },   // 1/2 puis 2/5 → 1/5, reste 3/10
    { a1: 3, b1: 8, c: 2, d: 3 },   // 3/8 puis 2/3 → 1/4, reste 3/8
];

const genPartageTrous = () => {
    const { a1, b1, c, d } = randomChoice(PARTAGES);
    const f1 = rat(a1, b1);
    const f2 = ratMul(f1, rat(c, d));
    const f3 = ratSub(ratSub(rat(1, 1), f1), f2);
    // Somme totale : multiple du PPCM des dénominateurs, en milliers d'euros.
    const ppcm = (x, y) => x * y / pgcd(x, y);
    const D = ppcm(ppcm(f1.q, f2.q), f3.q);
    const S = D * randInt(2, 9) * 250;
    const m1 = S * f1.p / f1.q; const m2 = S * f2.p / f2.q; const m3 = S * f3.p / f3.q;
    const variante = randomChoice(['fractions', 'montants']);
    let phrase; let texte;
    const intro = `<p>On partage ${String(S).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} €. `
        + `La première personne reçoit ${tex(ratFrag(f1))} de la somme, la deuxième reçoit `
        + `${tex(fracFrag(c, d))} de la part de la première, la troisième reçoit le reste.</p>`;
    if (variante === 'fractions') {
        phrase = `$\\text{2e personne : } \\dfrac{{{${f2.p}}}}{{{${f2.q}}}} \\quad\\text{3e personne : } \\dfrac{{{${f3.p}}}}{{{${f3.q}}}}$`;
        texte = intro + '<p><b>Quelles fractions de la somme reviennent à la 2e et à la 3e personne ?</b> '
            + 'Donne des fractions irréductibles.</p>';
    } else {
        phrase = `1re personne : {{${m1}}} € ; 2e personne : {{${m2}}} € ; 3e personne : {{${m3}}} €.`;
        texte = `${intro}<p>La part de la 2e personne vaut ${tex(ratFrag(f2))} de la somme, celle de la 3e vaut ${tex(ratFrag(f3))}.</p>`
            + '<p><b>Calcule la part de chacun en euros.</b> Écris les entiers sans espaces.</p>';
    }
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_PARTAGE), mode: 'saisie' });
    payload._meta.params = { f1, f2, f3, S, variante };
    return payload;
};

// ═════════ « remontee » — retrouver le tout (stade / salle d'étude) ═════════

const genRemonteeTrous = () => {
    const contexte = randomChoice(['stade', 'college', 'mediatheque']);
    let f1; let f2; let reste;
    for (let essai = 0; essai < 60; essai++) {
        f1 = rat(randInt(1, 6), randomChoice([7, 9, 11, 13]));
        f2 = rat(randInt(1, 4), randomChoice([5, 6, 9]));
        reste = ratSub(ratSub(rat(1, 1), f1), f2);
        if (reste.p > 0 && f1.q !== f2.q && reste.q <= 120 && reste.p < reste.q) break;
        reste = null;
    }
    if (!reste) { f1 = rat(6, 13); f2 = rat(4, 9); reste = ratSub(ratSub(rat(1, 1), f1), f2); } // repli (doc stade)
    const k = randInt(3, 40);
    const n = k * reste.p;          // effectif du reste
    const total = k * reste.q;      // le tout, ENTIER par construction
    const HABILLAGES = {
        stade: [`Dans un stade, ${tex(ratFrag(f1))} des spectateurs soutiennent l'équipe locale et ${tex(ratFrag(f2))} l'équipe adverse.`,
            `Les ${n} spectateurs restants sont neutres.`, 'spectateurs'],
        college: [`Dans un collège, ${tex(ratFrag(f1))} des élèves sont demi-pensionnaires et ${tex(ratFrag(f2))} sont externes.`,
            `Les ${n} élèves restants sont internes.`, 'élèves'],
        mediatheque: [`Dans une médiathèque, ${tex(ratFrag(f1))} des documents sont des romans et ${tex(ratFrag(f2))} des bandes dessinées.`,
            `Les ${n} documents restants sont des revues.`, 'documents'],
    };
    const [ph1, ph2, unite] = HABILLAGES[contexte];
    const deUnite = /^[aeéiouh]/.test(unite) ? `d'${unite}` : `de ${unite}`;   // élision
    // ⚠️ Phrase ENTIÈREMENT en KaTeX, libellés compris (`\text{…}`) : dès qu'une
    // ligne est full-math, `buildMathHtml` substitue le markup de slot dans
    // TOUTES les lignes, et une ligne restée en texte l'afficherait en clair
    // (« \htmlClass{tt-slot}{\phantom{00}} élèves »). Règle : tout en math, ou
    // rien — jamais un mélange.
    const phrase = `$\\text{Fraction restante : } \\dfrac{{{${reste.p}}}}{{{${reste.q}}}}`
        + ` \\qquad \\text{Total : } {{${total}}}\\ \\text{${unite}}$`;
    const payload = makeTexteTrou(phrase,
        `<p>${ph1} ${ph2}</p>`
        + `<p><b>Quelle fraction du total reste-t-il, et combien y a-t-il ${deUnite} en tout ?</b> `
        + 'Donne une fraction irréductible et un entier sans espaces.</p>',
        { ...opts(L_REMONTEE), mode: 'saisie' });
    payload._meta.params = { f1, f2, reste, n, total, contexte };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    somme_texte_trou_saisie: genSommeTrous,
    produit_texte_trou_saisie: genProduitTrous,
    quotient_texte_trou_saisie: genQuotientTrous,
    priorites_texte_trou_saisie: genPrioritesTrous,
    parentheses_tableau_vf: genParenthesesVf,
    piege_qcm_unique: genPiegeQcm,
    partage_texte_trou_saisie: genPartageTrous,
    remontee_texte_trou_saisie: genRemonteeTrous,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', texte_trou_saisie: 'Saisie trous', tableau_vf: 'Vrai/Faux',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'somme', label: L_SOMME, formatsList: buildFormats('somme') },
    { id: 'produit', label: L_PRODUIT, formatsList: buildFormats('produit') },
    { id: 'quotient', label: L_QUOTIENT, formatsList: buildFormats('quotient') },
    { id: 'priorites', label: L_PRIORITES, formatsList: buildFormats('priorites') },
    { id: 'parentheses', label: L_PARENTHESES, formatsList: buildFormats('parentheses') },
    { id: 'piege', label: L_PIEGE, formatsList: buildFormats('piege') },
    { id: 'partage', label: L_PARTAGE, formatsList: buildFormats('partage') },
    { id: 'remontee', label: L_REMONTEE, formatsList: buildFormats('remontee') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
